import { Inject, Injectable, Injector, OnDestroy, Optional } from '@angular/core';
import {
  ITrackingProvider,
  IVimCoreTrackingConfig,
  IVimCoreTrackingUserData,
  VimCoreTrackingData,
  VimCoreTrackingProviderBaseConfig,
  VimCoreTrackingProviderConfig,
} from '../interface';
import { ConsoleTrackingProvider } from '../provider/console';
import { NoopTrackingProvider } from '../provider/noop';
import { TRACKING_CONFIG } from '../const';
import _kebabCase from 'lodash-es/kebabCase';

interface ProviderConfig extends VimCoreTrackingProviderBaseConfig {
  provider: ITrackingProvider<unknown>;
}

@Injectable({ providedIn: 'root' })
export class VimCoreTrackingService implements OnDestroy {
  private providers: ProviderConfig[];
  private isEnabled = true;
  private additionalData: VimCoreTrackingData = {};
  private source: string;

  constructor(@Optional() @Inject(TRACKING_CONFIG) private config: IVimCoreTrackingConfig, private injector: Injector) {
    if (!this.config) {
      this.config = { enabled: false, debug: false, source: '', providers: [] };
    } else {
      if (!this.config.source) {
        throw new Error('VimCoreTrackingService: `source` config field is required');
      }
    }

    const providers: VimCoreTrackingProviderConfig[] = !this.config.enabled
      ? [{ useClass: NoopTrackingProvider }]
      : this.config.debug
      ? [{ useClass: ConsoleTrackingProvider }]
      : this.config.providers;

    this.providers = providers.map(providerConfig => ({
      ...providerConfig,
      provider: this.injector.get(providerConfig.useClass),
    }));

    this.forEachProvider(({ provider, useClass: _, ...initConfig }) => {
      provider.init(initConfig);

      this.setSource(this.config.source);
    });
  }

  ngOnDestroy(): void {}

  setUserData(userId: number, userData?: IVimCoreTrackingUserData): void {
    this.forEachProvider(({ provider }) => provider.setUserData(userId, userData));
  }

  setAdditionalData(additionalData: VimCoreTrackingData): void {
    this.additionalData = additionalData;
  }

  track(event: string, data: VimCoreTrackingData = {}): void {
    if (!this.isEnabled) {
      return;
    }

    this.forEachProvider(({ provider }) => provider.track(event, { ...this.additionalData, ...data }));
  }

  setIsEnabled(isEnabled: boolean): void {
    this.isEnabled = isEnabled;
  }

  page(pageId: string, pageContext: VimCoreTrackingData = {}): void {
    if (!this.isEnabled) {
      return;
    }

    this.forEachProvider(({ provider }) => provider.page(pageId, pageContext));
  }

  resetUser(): void {
    if (!this.isEnabled) {
      return;
    }

    this.forEachProvider(({ provider }) => provider.resetUser());
  }

  setSource(source: string): void {
    source = _kebabCase(source);

    this.source = source;

    this.forEachProvider(({ provider }) => provider.setSource(source));
  }

  getSource(): string {
    return this.source;
  }

  private forEachProvider(callback: (provider: ProviderConfig) => unknown): void {
    this.providers.forEach(provider => {
      // dont skip providers on any error
      try {
        callback(provider);
      } catch (error) {
        console.error(error);
      }
    });
  }
}
